/** One-time, same-origin migration of the historical client storage namespace. */
const LEGACY_ORIGINS = new Set([
  "https://kakehashi.cloud-ai-app.net",
  "https://kakehashi.skillbridges.app",
  "https://staging.kakehashi.cloud-ai-app.net",
  "https://staging.kakehashi.skillbridges.app",
  "https://staging.kakehashi-web.pages.dev",
]);
const MARKER = "ailechat:storage-migration:v1";
const DRAFT_MARKER = "ailechat:draft-migration:v1";
const AUTH_KEYS = ["session", "token", "auth-session"] as const;
const PREFERENCES: Record<string, readonly string[]> = {
  sidebarOpen: ["0", "1"], contextOpen: ["0", "1"], sidebarCollapsed: ["0", "1"],
  tone: ["polite", "casual", "honorific"], fontScale: ["100", "125", "150"],
  lang: ["ja-JP", "en-US", "zh-CN", "zh-TW", "ko-KR", "ja", "en", "zh", "ko"],
};

function removeAuth(storage: Storage, prefixes: readonly string[]): void {
  for (const prefix of prefixes) for (const key of AUTH_KEYS) {
    try { storage.removeItem(`${prefix}:${key}`); } catch { /* Try the remaining keys too. */ }
  }
}

/** A new login also supersedes any legacy migration in another tab. */
export function retireLegacyAuthStorage(storage?: Storage): void {
  try {
    const target = storage ?? window.localStorage;
    try { target.setItem(MARKER, "1"); } catch { /* Removing credentials can free quota. */ }
    removeAuth(target, ["kakehashi"]);
    try { target.setItem(MARKER, "1"); } catch { /* Storage may be disabled. */ }
  } catch { /* Access to storage itself may be disabled. */ }
}

/** Logout, rejected refresh and account deletion must also retire legacy credentials. */
export function clearAuthStorage(storage?: Storage): void {
  try {
    const target = storage ?? window.localStorage;
    retireLegacyAuthStorage(target);
    removeAuth(target, ["ailechat"]);
    retireLegacyAuthStorage(target);
  } catch { /* Access to storage itself may be disabled. */ }
}

function object(value: string | null): Record<string, any> | null {
  if (!value) return null;
  try { const parsed = JSON.parse(value); return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null; }
  catch { return null; }
}

function scope(token: string): { userId: string; tenantId: string } | null {
  try {
    const encoded = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const claims = JSON.parse(atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, "=")));
    return typeof claims.sub === "string" && claims.sub && typeof claims.tid === "string" && claims.tid
      ? { userId: claims.sub, tenantId: claims.tid } : null;
  } catch { return null; }
}

function legacyAuth(storage: Storage): Array<[string, string]> {
  // A partial new login still belongs to the new namespace. Never fill it from an old identity.
  if (AUTH_KEYS.some(key => storage.getItem(`ailechat:${key}`) !== null)) return [];
  const rawSession = storage.getItem("kakehashi:session"), rawAuth = storage.getItem("kakehashi:auth-session");
  const session = object(rawSession), auth = object(rawAuth), token = storage.getItem("kakehashi:token");
  if ((rawSession !== null && !session) || (rawAuth !== null && !auth) || (!session && !auth)) return [];
  const access = auth?.access_token ?? session?.token;
  if (typeof access !== "string" || !access || (token !== null && token !== access)) return [];
  const identity = scope(access);
  if (!identity) return [];
  if (session && (session.token !== access || session.user?.id !== identity.userId || typeof session.user?.email !== "string"
    || (session.tenantId !== undefined && session.tenantId !== identity.tenantId))) return [];
  if (auth && (auth.mfa_required === true || auth.user?.id !== identity.userId || auth.user?.tenant_id !== identity.tenantId
    || typeof auth.user?.email !== "string" || typeof auth.refresh_token !== "string" || !auth.refresh_token
    || auth.token_type !== "Bearer" || !Number.isFinite(auth.expires_at) || !Number.isFinite(auth.refresh_expires_at)
    || (session && session.user.email !== auth.user.email))) return [];
  const email = auth?.user.email ?? session!.user.email;
  const displayName = auth?.user.display_name ?? session?.user.displayName ?? email.split("@")[0];
  const values: Array<[string, string]> = [
    ["token", access],
    ["session", JSON.stringify({ token: access, tenantId: identity.tenantId,
      user: { id: identity.userId, email, displayName: typeof displayName === "string" ? displayName : email } })],
  ];
  if (auth) values.unshift(["auth-session", JSON.stringify({
    access_token: access, refresh_token: auth.refresh_token, token_type: "Bearer", expires_at: auth.expires_at,
    refresh_expires_at: auth.refresh_expires_at, user: auth.user, stored_at: Number.isFinite(auth.stored_at) ? auth.stored_at : Date.now(),
  })]);
  return values;
}

function migrateLocal(storage: Storage): void {
  if (storage.getItem(MARKER) !== null) { removeAuth(storage, ["kakehashi"]); return; }
  const auth = legacyAuth(storage);
  // Commit the retirement first: interrupted migrations must not resurrect credentials later.
  const owner = `migrating:${Date.now()}:${Math.random()}`;
  storage.setItem(MARKER, owner);
  const written: Array<[string, string]> = [];
  try {
    for (const [key, value] of auth) {
      if (storage.getItem(MARKER) !== owner) throw new Error("Migration was retired by another session operation");
      storage.setItem(`ailechat:${key}`, value); written.push([`ailechat:${key}`, value]);
      if (storage.getItem(MARKER) !== owner) throw new Error("Migration was retired by another session operation");
    }
  } catch {
    for (const [key, value] of written) {
      try { if (storage.getItem(key) === value) storage.removeItem(key); } catch { /* Best effort after a storage failure. */ }
    }
  } finally {
    if (storage.getItem(MARKER) === owner) storage.setItem(MARKER, "1");
    removeAuth(storage, ["kakehashi"]);
  }
  for (const [key, allowed] of Object.entries(PREFERENCES)) {
    const value = storage.getItem(`kakehashi:${key}`);
    if (value !== null && allowed.includes(value) && storage.getItem(`ailechat:${key}`) === null) storage.setItem(`ailechat:${key}`, value);
    storage.removeItem(`kakehashi:${key}`);
  }
}

function migrateDrafts(storage: Storage): void {
  if (storage.getItem(DRAFT_MARKER) === "1") return;
  const keys = Array.from({ length: storage.length }, (_, index) => storage.key(index));
  for (const key of keys) {
    if (!key || !/^kakehashi:draft:[^:]+:[^:]+:[^:]+$/.test(key)) continue;
    const value = storage.getItem(key), next = key.replace(/^kakehashi:/, "ailechat:");
    if (value !== null && storage.getItem(next) === null) storage.setItem(next, value);
    storage.removeItem(key);
  }
  storage.setItem(DRAFT_MARKER, "1");
}

export function migrateLegacyStorage(context?: { origin: string; local: Storage; session: Storage }): void {
  try {
    const origin = context?.origin ?? window.location.origin;
    if (!LEGACY_ORIGINS.has(origin)) return;
    try { migrateLocal(context?.local ?? window.localStorage); } catch { /* Migration must not block startup. */ }
    try { migrateDrafts(context?.session ?? window.sessionStorage); } catch { /* Retry unfinished drafts next load. */ }
  } catch { /* No browser storage in SSR or restricted contexts. */ }
}

// main.tsx imports this before App, whose i18n initialization reads and caches language.
migrateLegacyStorage();
